import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
// eslint-disable-next-line no-unused-vars
import { FaBars, FaTimes, FaUser, FaFileAlt, FaListAlt, FaPlusCircle, FaHistory, FaCalendarAlt, FaClock, FaFileContract, FaMoneyBillWave, FaSignOutAlt, FaChevronDown, FaHome, FaUpload, FaList, FaArrowRight, FaUserPlus, FaClipboardCheck, FaEye, FaUsers, FaCog, FaBook, FaGraduationCap, FaBriefcase, FaLanguage, FaAward, FaPhone, FaUserTie, FaExclamationTriangle, FaCheckCircle, FaSyncAlt } from 'react-icons/fa';
import Toast from './Toast';
import * as authService from '../services/authService';
import * as articleService from '../services/articleService';
import * as paymentService from '../services/paymentService';
import * as adminService from '../services/adminService';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(authService.isLoggedIn());
    const [openDropdown, setOpenDropdown] = useState(null);
    const [currentUser, setCurrentUser] = useState(authService.getCurrentUser());
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [toast, setToast] = useState(null);
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const [isProcessingPayment, setIsProcessingPayment] = useState(false);
    const [runningTitlesList, setRunningTitlesList] = useState([]);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [pendingNavPath, setPendingNavPath] = useState(null);
    const [signupPrice, setSignupPrice] = useState(null);
    const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
    const [paymentSuccessNavPath, setPaymentSuccessNavPath] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();

    const canReview = authService.isVerifiedReviewer(currentUser);
    const isVerifiedEditorial = authService.isVerifiedEditorial(currentUser);
    const canEditorial = isVerifiedEditorial;
    const isAdmin = currentUser?.role === 'admin' || currentUser?.role_code === 'admin';

    // Helper to get display label based on role and verification status
    const getUserRoleLabel = (user) => {
        if (!user) return '';
        const role = user.role || user.role_code;
        const isVerifiedEB = authService.isVerifiedEditorial(user);

        switch (role) {
            case 'graduate': return 'Graduate';
            case 'post-graduate': return 'Post Graduate';
            case 'phd': return 'Researcher';
            case 'professor':
                return isVerifiedEB ? 'EB Member' : 'Professor';
            case 'reviewer':
                return authService.isVerifiedReviewer(user) ? 'Reviewer' : 'Professor';
            case 'editorial':
                return 'EB Member';
            case 'admin': return 'Admin';
            default: return '';
        }
    };

    const roleLabel = getUserRoleLabel(currentUser);
    const displayName = currentUser ? `${currentUser.full_name || currentUser.name || currentUser.username || 'User'}${roleLabel ? ` (${roleLabel})` : ''}` : 'User';

    // Check if we're on the home page
    const isHomePage = location.pathname === '/';

    // Check login status
    useEffect(() => {
        const fetchAllRunningTitles = async () => {
            try {
                const data = await articleService.getRunningTitles();
                const titlesArray = Array.isArray(data) ? data : (data?.runningTitles || data?.data || []);
                setRunningTitlesList(titlesArray);
            } catch (error) {
                console.error('Error fetching running titles for marquee:', error);
            }
        };

        fetchAllRunningTitles();
        const checkLoginStatus = () => {
            const user = authService.getCurrentUser();
            if (user) {
                setIsLoggedIn(true);
                setCurrentUser(user);
            } else {
                setIsLoggedIn(false);
                setCurrentUser(null);
            }
        };

        checkLoginStatus();
        window.addEventListener('userLogin', checkLoginStatus);
        window.addEventListener('userLogout', checkLoginStatus);

        return () => {
            window.removeEventListener('userLogin', checkLoginStatus);
            window.removeEventListener('userLogout', checkLoginStatus);
        };
    }, []);

    // Fetch signup price for PENDING users
    useEffect(() => {
        const fetchSignupPrice = async () => {
            try {
                const res = await paymentService.getSignupPrice();
                if (res.success && res.price !== undefined) {
                    setSignupPrice(Number(res.price) || 0);
                }
            } catch (err) {
                console.error('Failed to fetch signup price:', err);
            }
        };
        if (currentUser?.payment_status === 'PENDING') {
            fetchSignupPrice();
        }
    }, [currentUser?.payment_status]);

    // Auto-show payment modal after signup redirect (?showPayment=1)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('showPayment') === '1' && currentUser?.payment_status === 'PENDING') {
            setShowPaymentModal(true);
            // Clean the URL without triggering a re-render/navigation
            const cleanUrl = location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        }
    }, [location.search, currentUser?.payment_status]);

    // Handle click outside to close dropdowns and mobile menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            // Only stay open if clicking inside a dropdown container or the mobile toggle
            const isClickInsideMenu = event.target.closest('.relative.group') ||
                event.target.closest('.mobile-toggle') ||
                event.target.closest('.mobile-menu');

            if (!isClickInsideMenu) {
                setOpenDropdown(null);
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleSyncStatus = async () => {
        const lastOrderId = localStorage.getItem('last_razorpay_order_id');
        if (!lastOrderId) {
            showToast('No recent order found to sync. Please proceed to payment.', 'info');
            return;
        }

        setIsProcessingPayment(true);
        try {
            const res = await paymentService.syncPaymentStatus(lastOrderId);
            if (res.success) {
                showToast('Payment verified successfully!', 'success');
                const updatedUser = { ...currentUser, payment_status: 'PAID' };
                authService.storeSession(updatedUser, null, null, !!localStorage.getItem('paperPortalUser'));
                setCurrentUser(updatedUser);
                window.dispatchEvent(new Event('userLogin'));
                setShowPaymentModal(false);
                if (pendingNavPath) navigate(pendingNavPath);
            } else {
                showToast(res.message || 'Payment not yet confirmed. Please try again.', 'warning');
            }
        } catch (err) {
            showToast(err.message || 'Error syncing payment status', 'error');
        } finally {
            setIsProcessingPayment(false);
        }
    };

    const slides = [
        {
            title: 'Transform Your Academics',
            subtitle: 'Research and Collaborate with the Young India Innovation Hub',
            gradient: 'from-blue-600 via-blue-500 to-cyan-500',
            video: '/istockphoto-1542748041-640_adpp_is.mp4'
        },
        {
            title: 'Connect With Academics',
            subtitle: 'Join a Community of Researchers and Scholars',
            gradient: 'from-indigo-600 via-purple-500 to-pink-500',
            video: '/istockphoto-1542748041-640_adpp_is.mp4'
        },
        {
            title: 'Publish Your Research',
            subtitle: 'Submit Papers, Articles, and Abstracts with Ease',
            gradient: 'from-cyan-600 via-blue-500 to-indigo-500',
            video: '/istockphoto-1542748041-640_adpp_is.mp4'
        }
    ];

    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        if (isHomePage) {
            const timer = setInterval(() => {
                setCurrentSlide((prev) => (prev + 1) % slides.length);
            }, 5000);
            return () => clearInterval(timer);
        }
    }, [slides.length, isHomePage]);


    // Get Young India submenus based on user role
    const getYoungIndiaSubmenus = () => {
        if (!currentUser) return [];

        if (isAdmin) {
            return [
                { id: 'yi-create-title', label: 'Create and View Article', path: '/create-title', icon: FaUpload },
                { id: 'yi-pending-titles', label: 'Pending Approvals', path: '/admin/pending-titles', icon: FaClipboardCheck },
                { id: 'yi-selected-students', label: 'Show Selected Students', path: '/selected-students', icon: FaUsers },
            ];
        }

        // Editorial Board members (authorized status represented by canEditorial flag)
        // see "All Articles" in the Young India section
        if (canEditorial) {
            return [
                { id: 'yi-manage-titles', label: 'Create and View Article', path: '/eb/manage-titles', icon: FaPlusCircle },
                { id: 'yi-all-articles', label: 'All Articles', path: '/all-articles-young-india', icon: FaListAlt },
                // { id: 'yi-submit-article', label: 'Submit Article', path: '/create-article-title-yi', icon: FaPlusCircle },
                // { id: 'yi-my-submissions', label: 'My Submissions', path: '/my-submitted-articles-young-india', icon: FaHistory },
            ];
        }

        // ❌ Reviewers NEVER see Young India
        if (canReview) return [];

        // Contributor roles (Graduate, Post Graduate, PhD, Professor, Researcher)
        const contributorRoles = ['graduate', 'post-graduate', 'phd'];
        if (contributorRoles.includes(currentUser.role)) {
            return [
                { id: 'yi-submit-article', label: 'Contest Article', path: '/submit-article-young-india', icon: FaUpload },
                // { id: 'yi-custom-submission', label: 'Custom Submission', path: '/custom-submission', icon: FaUpload },
                { id: 'yi-my-articles', label: 'My Submitted Articles', path: '/my-submitted-articles-young-india', icon: FaList },
            ];
        }

        return [];
    };


    // Registration options visible in navbar
    const registrationOptions = [
        {
            id: 'young-india',
            label: 'Young India',
            shortLabel: 'Young India',
            description: 'Graduate & Post Graduate',
            path: '/signup?type=young-india',
            icon: FaGraduationCap,
            gradient: 'from-green-500 to-emerald-600'
        },
        {
            id: 'researcher',
            label: 'Register as Researcher',
            shortLabel: 'Researcher',
            description: 'PhD Scholars & Researchers',
            path: '/signup?type=professor&role=researcher',
            icon: FaUserTie,
            gradient: 'from-blue-500 to-indigo-600'
        },
        {
            id: 'professor',
            label: 'Register as Professor',
            shortLabel: 'Professor',
            description: 'Academic Professors',
            path: '/signup?type=professor&role=professor',
            icon: FaBook,
            gradient: 'from-indigo-500 to-purple-600'
        },
        {
            id: 'reviewer',
            label: 'Register as Reviewer',
            shortLabel: 'Reviewer',
            description: 'Scientific Reviewers',
            path: '/signup?type=professor&role=reviewer',
            icon: FaClipboardCheck,
            gradient: 'from-purple-500 to-pink-600'
        },
        {
            id: 'editorial',
            label: 'Register as EB Member',
            shortLabel: 'EB Member',
            description: 'Editorial Board Members',
            path: '/signup?type=professor&role=editorial',
            icon: FaUsers,
            gradient: 'from-pink-500 to-rose-600'
        }
    ];

    const youngIndiaSubmenus = getYoungIndiaSubmenus();

    const isPendingPayment = currentUser?.payment_status === 'PENDING';

    // Paths freely accessible even when payment is PENDING
    const freeAccessPaths = ['/', '/profile', '/about-paper-portal', '/editorial-board'];
    const freeAccessIds = ['home', 'profile', 'about-us'];

    const menuItems = [
        // Always visible: Home, About Us, My Profile
        { id: 'home', label: 'Home', path: '/', icon: FaHome },
        {
            id: 'about-us',
            label: 'About Us',
            icon: FaBook,
            subItems: [
                { id: 'about-paper-portal', label: 'About Paper Portal', path: '/about-paper-portal', icon: FaFileAlt },
                { id: 'editorial-board', label: 'Editorial Board', path: '/editorial-board', icon: FaUsers },
            ]
        },
        { id: 'profile', label: 'My Profile', path: '/profile', icon: FaUser, protected: true },

        // All role-based menus are now always visible (gated by payment modal on click)
        // Young India Section - visible for everyone (protected for guests)
        ...(youngIndiaSubmenus.length > 0 || !isLoggedIn ? [{
            id: 'young-india',
            label: 'Young India',
            icon: FaUser,
            path: '/young-india',
            protected: true,
            subItems: youngIndiaSubmenus
        }] : []),

        ...(isLoggedIn ? [{ id: 'payment', label: 'My Payment', path: '/my-payment', icon: FaMoneyBillWave, protected: true }] : []),

        ...(isLoggedIn && currentUser ? [
            // Submissions Group for Papers, Abstracts, and Articles
            ...(currentUser.role !== 'admin' && currentUser.role !== 'graduate' && currentUser.role !== 'post-graduate' ? [
                {
                    id: 'submissions',
                    label: 'Submissions',
                    icon: FaFileAlt,
                    subItems: [
                        { id: 'submit-abstract', label: 'Submit Abstract', path: '/submit-abstract', icon: FaUpload },
                        { id: 'my-abstract', label: 'My Abstract', path: '/my-abstract', icon: FaList },
                        { id: 'submit-paper', label: 'Submit Paper', path: '/submit-paper', icon: FaUpload },
                        { id: 'my-paper', label: 'My Paper', path: '/my-paper', icon: FaList },
                    ]
                }
            ] : []),

            // Admin Management Group
            ...(currentUser.role === 'admin' ? [
                {
                    id: 'admin-management',
                    label: 'Management',
                    icon: FaList,
                    subItems: [
                        { id: 'professor-management', label: 'Professor Management', path: '/professor-management', icon: FaUsers },
                        { id: 'reviewer-management', label: 'Reviewer Management', path: '/reviewer-management', icon: FaClipboardCheck },
                        { id: 'editorial-management', label: 'Editorial Management', path: '/editorial-management', icon: FaUserTie },
                        { id: 'sub-eb-requests', label: 'Sub-EB Requests', path: '/admin-sub-eb-requests', icon: FaUsers },
                        { id: 'create-subject', label: 'Create Department', path: '/create-subject', icon: FaBook },
                        { id: 'create-stream', label: 'Create Stream', path: '/create-stream', icon: FaGraduationCap },
                        { id: 'create-specialization', label: 'Create Specialization', path: '/create-specialization', icon: FaBriefcase },
                        { id: 'create-language', label: 'Create Language', path: '/create-language', icon: FaLanguage },
                        { id: 'assign-abstract', label: 'Assign Abstract', path: '/assign-abstract', icon: FaUpload },
                        { id: 'assign-paper', label: 'Assign Paper', path: '/assign-paper', icon: FaUpload },
                        { id: 'authorized-numbers', label: 'Authorized Numbers', path: '/authorized-numbers', icon: FaPhone },
                        { id: 'yi-student-management', label: 'Young India Students', path: '/yi-student-management', icon: FaUsers },
                    ]
                },
                {
                    id: 'admin-settings-group',
                    label: 'Settings',
                    icon: FaCog,
                    subItems: [
                        { id: 'payment-management', label: 'Transactions', path: '/admin-payment-management', icon: FaMoneyBillWave },
                        { id: 'admin-settings', label: 'Registration Payment', path: '/admin-settings', icon: FaCog },
                        { id: 'admin-yi-payment-settings', label: 'YI Article Payment', path: '/admin-yi-payment-settings', icon: FaFileAlt },
                    ]
                }
            ] : []),


            // Review Group for Reviewers/Professors assigned as reviewers OR Editorial Board members
            // Show Reviews menu if:
            // 1. User is NOT an Admin (Admin has their own Management menu)
            // 2. User is a verified reviewer OR editorial board member
            ...((canReview || canEditorial) && !isAdmin ? [{
                id: 'reviews',
                label: 'Reviews',
                icon: FaClipboardCheck,
                subItems: [
                    // { id: 'assigned-article', label: 'Assigned Article', path: '/assigned-article', icon: FaFileAlt },
                    { id: 'assigned-abstract', label: 'Assigned Abstract', path: '/assigned-abstract', icon: FaFileContract },
                    { id: 'assigned-paper', label: 'Assigned Paper', path: '/assigned-paper', icon: FaFileAlt },
                    ...(!!currentUser?.is_editorial_board || currentUser?.is_editorial_board === 1 || isAdmin ? [{ id: 'sub-eb-selection', label: 'Member Selection', path: '/sub-eb-selection', icon: FaUserPlus }] : []),
                ]
            }] : []),
        ] : []),
    ];

    const isActive = (path) => location.pathname === path;

    const isDropdownActive = (item) => {
        if (item.subItems) {
            return item.subItems.some(sub => isActive(sub.path));
        }
        return false;
    };

    const toggleDropdown = (id) => {
        const isOpening = openDropdown !== id;
        setOpenDropdown(isOpening ? id : null);
    };

    const showToast = (message, type = 'success') => {
        setToast({ message, type });
        setTimeout(() => setToast(null), 3000);
    };

    const handleLogoutClick = () => {
        setShowLogoutConfirm(true);
    };

    const confirmLogout = async () => {
        setIsLoggingOut(true);

        try {
            const response = await authService.logoutUser();

            if (!response.success) {
                console.error('Logout API reported failure');
            }

            // Update state
            setIsLoggedIn(false);
            setCurrentUser(null);

            showToast('You have been logged out successfully', 'success');

        } catch (error) {
            console.error('Error during logout:', error);
            setIsLoggedIn(false);
            setCurrentUser(null);
            showToast('Logged out successfully', 'success');
        } finally {
            setIsLoggingOut(false);
            setShowLogoutConfirm(false);
            setIsOpen(false);
            setOpenDropdown(null);

            navigate('/');
        }
    };

    const cancelLogout = () => {
        setShowLogoutConfirm(false);
    };

    const handleNavigation = () => {
        setIsOpen(false);
        setOpenDropdown(null);
    };

    const handleSignupPayment = async (targetPath) => {
        if (isProcessingPayment) return;
        setIsProcessingPayment(true);

        try {
            // 1. Create Order
            const orderRes = await paymentService.createSignupOrder();

            if (orderRes.success && orderRes.auto_paid) {
                // Refresh user data
                const updatedUser = { ...currentUser, payment_status: 'PAID' };
                authService.storeSession(updatedUser, null, null, !!localStorage.getItem('paperPortalUser'));
                setCurrentUser(updatedUser);
                window.dispatchEvent(new Event('userLogin'));
                setIsProcessingPayment(false);
                setShowPaymentModal(false);
                setPaymentSuccessNavPath(targetPath || '/');
                setShowPaymentSuccessModal(true);
                return;
            }

            const { order_id, amount, key_id, user } = orderRes.data;

            // Store order ID for potential sync recovery
            localStorage.setItem('last_razorpay_order_id', order_id);

            // 2. Open Razorpay
            const options = {
                key: key_id,
                amount: amount,
                currency: "INR",
                name: "Paper Portal",
                description: "Registration Fee",
                order_id: order_id,
                handler: async function (response) {
                    try {
                        const verifyRes = await paymentService.verifySignupPayment({
                            razorpay_order_id: response.razorpay_order_id,
                            razorpay_payment_id: response.razorpay_payment_id,
                            razorpay_signature: response.razorpay_signature
                        });

                        if (verifyRes.success) {
                            // Refresh local user data
                            const updatedUser = { ...currentUser, payment_status: 'PAID' };
                            authService.storeSession(updatedUser, null, null, !!localStorage.getItem('paperPortalUser')); https://paperportal.in/
                            setCurrentUser(updatedUser);
                            window.dispatchEvent(new Event('userLogin')); // Trigger re-render across app
                            setShowPaymentModal(false);
                            setPaymentSuccessNavPath(targetPath || pendingNavPath || '/');
                            setShowPaymentSuccessModal(true);
                        } else {
                            showToast(verifyRes.error || 'Payment verification failed', 'error');
                        }
                    } catch (err) {
                        showToast(err.message || 'Verification error', 'error');
                    } finally {
                        setIsProcessingPayment(false);
                    }
                },
                prefill: {
                    name: user.name,
                    email: user.email,
                    contact: user.phone
                },
                theme: {
                    color: "#312e81"
                },
                modal: {
                    ondismiss: function () {
                        setIsProcessingPayment(false);
                    }
                }
            };

            const rzp = new window.Razorpay(options);
            rzp.open();

        } catch (error) {
            showToast(error.message || 'Failed to initiate payment', 'error');
            setIsProcessingPayment(false);
        }
    };

    // Check if a path/item is restricted for PENDING payment users
    const isRestrictedForPending = (item) => {
        if (!isPendingPayment) return false;
        if (freeAccessIds.includes(item.id)) return false;
        if (item.path && freeAccessPaths.includes(item.path)) return false;
        return true;
    };

    const handleProtectedClick = (e, item) => {
        if (item.protected && !isLoggedIn) {
            e.preventDefault();
            setShowAuthModal(true);
            return;
        }

        // If user is pending payment, show payment modal for restricted items
        if (isRestrictedForPending(item)) {
            e.preventDefault();
            setPendingNavPath(item.path || '/');
            setShowPaymentModal(true);
            return;
        }

        handleNavigation();
    };

    // Handle sub-item clicks in dropdowns for PENDING users
    const handleSubItemClick = (e, subItem, parentItem) => {
        if (isPendingPayment && !freeAccessPaths.includes(subItem.path) && !freeAccessIds.includes(parentItem?.id)) {
            e.preventDefault();
            setPendingNavPath(subItem.path);
            setShowPaymentModal(true);
            return;
        }
        handleNavigation();
    };

    return (
        <>
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {showLogoutConfirm && (
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaSignOutAlt className="w-8 h-8 text-red-600" />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">Confirm Logout</h3>
                            <p className="text-gray-600">Are you sure you want to logout?</p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={cancelLogout}
                                disabled={isLoggingOut}
                                className="flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold hover:bg-gray-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmLogout}
                                disabled={isLoggingOut}
                                className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg font-semibold hover:from-red-600 hover:to-red-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {isLoggingOut ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Logging out...
                                    </>
                                ) : (
                                    'Yes, Logout'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Payment Modal for PENDING users */}
            {showPaymentModal && isPendingPayment && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[100] p-4">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
                        {/* Header gradient */}
                        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-8 py-6 text-center">
                            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-4">
                                <FaMoneyBillWave className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-2xl font-extrabold text-white mb-1">Complete Activation</h3>
                            <p className="text-white/80 text-sm">Unlock full access to all platform features</p>
                        </div>

                        {/* Pricing section */}
                        <div className="px-8 py-8">
                            <div className="text-center mb-6">
                                <div className="flex items-center justify-center gap-4 mb-2">
                                    <span className="text-3xl font-bold text-gray-300 line-through decoration-red-500 decoration-2">₹999</span>
                                    <span className="text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-600">
                                        ₹{signupPrice !== null ? signupPrice : '...'}
                                    </span>
                                </div>
                                {signupPrice !== null && (
                                    <span className="inline-block bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full">
                                        🎉 50% off till 31st march
                                    </span>
                                )}
                            </div>

                            <div className="space-y-3 mb-6">
                                <div className="flex items-center gap-3 text-sm text-gray-600">
                                    <FaCheckCircle className="text-green-500 flex-shrink-0" />
                                    <span>Young India contest participation</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm text-gray-600">
                                    <FaCheckCircle className="text-green-500 flex-shrink-0" />
                                    <span>Access to all submissions & reviews</span>
                                </div>
                                <div className="flex items-center gap-3 text-sm text-gray-600">
                                    <FaCheckCircle className="text-green-500 flex-shrink-0" />
                                    <span>Payment history & management</span>
                                </div>
                            </div>

                            <button
                                onClick={() => handleSignupPayment(pendingNavPath)}
                                disabled={isProcessingPayment}
                                className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-2xl font-bold text-lg hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            >
                                {isProcessingPayment ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <FaMoneyBillWave />
                                        Pay Now — ₹{signupPrice !== null ? signupPrice : '...'}
                                    </>
                                )}
                            </button>

                            <button
                                onClick={() => { setShowPaymentModal(false); setPendingNavPath(null); }}
                                className="w-full mt-3 py-3 text-gray-500 hover:text-gray-700 font-medium text-sm transition-colors"
                            >
                                Maybe Later
                            </button>

                            {localStorage.getItem('last_razorpay_order_id') && (
                                <div className="mt-6 pt-6 border-t border-gray-100">
                                    <p className="text-[11px] text-gray-400 text-center mb-3">
                                        Already paid but account not active?
                                    </p>
                                    <button
                                        onClick={handleSyncStatus}
                                        disabled={isProcessingPayment}
                                        className="w-full py-2.5 bg-gray-100 text-gray-700 rounded-xl text-sm font-bold hover:bg-gray-200 transition-all flex items-center justify-center gap-2"
                                    >
                                        <FaSyncAlt className={isProcessingPayment ? 'animate-spin' : ''} />
                                        Refresh Payment Status
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {isHomePage ? (
                <div className="relative">
                    <div className="absolute inset-0 h-[calc(600px+6rem)]">
                        <div className="absolute inset-0">
                            <video
                                autoPlay
                                loop
                                muted
                                playsInline
                                className="w-full h-full object-cover"
                            >
                                <source src="/istockphoto-1542748041-640_adpp_is.mp4" type="video/mp4" />
                            </video>
                            <div className="absolute inset-0 bg-black/40"></div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/60"></div>
                            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-transparent"></div>
                        </div>
                    </div>

                    <nav className="relative z-[45] pt-4">
                        <div className="flex items-center h-16 lg:h-20 w-full px-4 sm:px-6 lg:px-8 min-[1201px]:px-12">
                            <div className="flex items-center flex-shrink-0 mr-4 lg:mr-8">
                                <Link to="/" className="flex items-center hover:opacity-90 transition-opacity" onClick={handleNavigation}>
                                    <img src="/logo.png" alt="Paper Portal" className="h-8 sm:h-10 lg:h-12 mr-2 sm:mr-3 lg:mr-4" />
                                </Link>
                            </div>

                            <div className="flex-1 min-[1201px]:hidden" />

                            <ul className="hidden min-[1201px]:flex items-center justify-center flex-1 gap-x-1 2xl:gap-x-2">
                                {menuItems.map((item) => {
                                    if (item.subItems) {
                                        const isItemActive = isDropdownActive(item);
                                        const hasSubItems = item.subItems.length > 0;

                                        return (
                                            <li key={item.id} className="relative group">
                                                <button
                                                    onClick={(e) => {
                                                        if (item.protected && !isLoggedIn) {
                                                            setShowAuthModal(true);
                                                        } else {
                                                            toggleDropdown(item.id);
                                                        }
                                                    }}
                                                    className={`flex items-center gap-x-1.5 px-3 py-2.5 rounded-xl text-base font-bold transition-all duration-300 whitespace-nowrap backdrop-blur-sm ${isItemActive || openDropdown === item.id
                                                        ? 'bg-white/20 text-white shadow-lg'
                                                        : 'text-white/90 hover:text-white hover:bg-white/10 hover:shadow-lg'
                                                        }`}
                                                >
                                                    <item.icon className="w-4 h-4" />
                                                    <span>{item.label}</span>
                                                    {hasSubItems && (
                                                        <FaChevronDown className={`w-3 h-3 transition-transform ${openDropdown === item.id ? 'rotate-180' : ''}`} />
                                                    )}
                                                </button>

                                                {openDropdown === item.id && hasSubItems && (
                                                    <ul className="absolute left-0 top-full mt-2 w-64 bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl py-2 border border-white/20 z-50">
                                                        {item.subItems.map((subItem) => (
                                                            <li key={subItem.id}>
                                                                <Link
                                                                    to={subItem.path}
                                                                    onClick={(e) => handleSubItemClick(e, subItem, item)}
                                                                    className={`flex items-center gap-x-3 px-4 py-3 text-base font-semibold transition-all duration-200 ${isActive(subItem.path)
                                                                        ? 'bg-gradient-to-r from-blue-50/80 to-primary-blue/20 text-primary-blue border-r-4 border-primary-blue'
                                                                        : 'text-gray-700 hover:bg-blue-50/80 hover:text-primary-blue'
                                                                        }`}
                                                                >
                                                                    <subItem.icon className="w-4 h-4" />
                                                                    <span>{subItem.label}</span>
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </li>
                                        );
                                    }

                                    return (
                                        <li key={item.id}>
                                            <Link
                                                to={item.path}
                                                onClick={(e) => handleProtectedClick(e, item)}
                                                className={`flex items-center gap-x-1.5 px-3 py-2.5 rounded-xl text-base font-bold transition-all duration-300 whitespace-nowrap backdrop-blur-sm ${isActive(item.path)
                                                    ? 'bg-white/20 text-white shadow-lg'
                                                    : 'text-white/90 hover:text-white hover:bg-white/10 hover:shadow-lg'
                                                    }`}
                                            >
                                                <item.icon className="w-4 h-4" />
                                                <span>{item.label}</span>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>

                            <div className="flex-1 min-[1201px]:hidden" />

                            <div className="hidden min-[1201px]:flex items-center gap-x-2 flex-shrink-0">
                                {!isLoggedIn ? (
                                    <div className="flex items-center gap-x-2">
                                        <div className="relative group">
                                            <button
                                                onClick={() => toggleDropdown('register-dropdown')}
                                                className={`flex items-center gap-x-1.5 px-5 py-3 rounded-xl text-base font-black transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap backdrop-blur-sm ${openDropdown === 'register-dropdown'
                                                    ? 'bg-white text-primary-blue shadow-lg'
                                                    : 'bg-primary-blue text-white hover:bg-blue-600'
                                                    }`}
                                            >
                                                <FaUserPlus className="w-4 h-4" />
                                                <span>Register</span>
                                                <FaChevronDown className={`w-3 h-3 transition-transform ${openDropdown === 'register-dropdown' ? 'rotate-180' : ''}`} />
                                            </button>

                                            {openDropdown === 'register-dropdown' && (
                                                <ul className="absolute right-0 top-full mt-2 w-64 bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl py-2 border border-white/20 z-50 animate-fade-in">
                                                    {registrationOptions.map((opt) => (
                                                        <li key={opt.id}>
                                                            <Link
                                                                to={opt.path}
                                                                onClick={handleNavigation}
                                                                className="flex items-center gap-x-3 px-4 py-3.5 text-base font-semibold text-gray-700 hover:bg-blue-50/80 hover:text-primary-blue transition-all duration-200"
                                                            >
                                                                <opt.icon className="w-4 h-4 text-primary-blue" />
                                                                <span>{opt.label}</span>
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                        <Link
                                            to="/login"
                                            onClick={handleNavigation}
                                            className="flex items-center gap-x-1.5 px-5 py-3 rounded-xl text-base font-black bg-white/95 backdrop-blur-sm text-primary-blue hover:bg-white transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                                        >
                                            Login
                                        </Link>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <div className="flex items-center gap-x-2">
                                            <div className="text-white/90 text-sm font-medium backdrop-blur-sm bg-white/10 px-3 py-2 rounded-lg">
                                                <span className="font-semibold">{displayName}</span>
                                            </div>
                                            <button
                                                onClick={handleLogoutClick}
                                                className="flex items-center gap-x-1.5 px-5 py-3 rounded-xl text-base font-black bg-gradient-to-r from-red-500/95 to-red-600/95 backdrop-blur-sm text-white hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                                            >
                                                <FaSignOutAlt className="w-4 h-4" />
                                                <span>Logout</span>
                                            </button>
                                        </div>
                                        {isPendingPayment && (
                                            <div
                                                onClick={() => { setPendingNavPath(null); setShowPaymentModal(true); }}
                                                className="absolute right-0 top-full mt-2 w-64 bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl shadow-2xl p-3 cursor-pointer hover:from-teal-600 hover:to-cyan-600 transition-all z-50"
                                            >
                                                <p className="text-white/90 text-[11px] font-medium mb-1.5">⚠️ Activation payment pending</p>
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-white/60 line-through text-xs">₹999</span>
                                                        <span className="text-white font-extrabold text-lg">₹{signupPrice !== null ? signupPrice : '...'}</span>
                                                    </div>
                                                    <span className="px-3 py-1 bg-white text-teal-600 rounded-full text-xs font-bold shadow">Pay Now →</span>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div className="min-[1201px]:hidden ml-4 mobile-toggle">
                                <button
                                    onClick={() => setIsOpen(!isOpen)}
                                    className="p-3 rounded-xl text-white hover:bg-white/10 transition-all duration-300 backdrop-blur-sm"
                                >
                                    {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
                                </button>
                            </div>
                        </div>

                        {isOpen && (
                            <div className="min-[1201px]:hidden absolute left-0 right-0 top-full z-40 mx-4 border border-white/20 bg-black/90 backdrop-blur-md shadow-2xl rounded-xl mt-2 mobile-menu">
                                <div className="px-4 pt-4 pb-6 space-y-2 max-h-[80vh] overflow-y-auto">
                                    {menuItems.map((item) => {
                                        if (item.subItems) {
                                            const isItemActive = isDropdownActive(item);
                                            const hasSubItems = item.subItems.length > 0;

                                            return (
                                                <div key={item.id}>
                                                    <button
                                                        onClick={() => toggleDropdown(item.id)}
                                                        className={`flex items-center justify-between w-full px-4 py-4 rounded-xl text-lg font-bold transition-all backdrop-blur-sm ${isItemActive || openDropdown === item.id
                                                            ? 'bg-white/20 text-white'
                                                            : 'text-white/90 hover:text-white hover:bg-white/10'
                                                            }`}
                                                    >
                                                        <div className="flex items-center gap-x-3">
                                                            <item.icon className="w-5 h-5" />
                                                            <span>{item.label}</span>
                                                        </div>
                                                        {hasSubItems && (
                                                            <FaChevronDown className={`w-4 h-4 transition-transform ${openDropdown === item.id ? 'rotate-180' : ''}`} />
                                                        )}
                                                    </button>

                                                    {openDropdown === item.id && hasSubItems && (
                                                        <div className="pl-8 mt-2 space-y-2">
                                                            {item.subItems.map((subItem) => (
                                                                <Link
                                                                    key={subItem.id}
                                                                    to={subItem.path}
                                                                    onClick={(e) => handleSubItemClick(e, subItem, item)}
                                                                    className={`flex items-center gap-x-3 px-4 py-3.5 rounded-xl text-base font-semibold transition-all backdrop-blur-sm ${isActive(subItem.path)
                                                                        ? 'bg-white/20 text-white'
                                                                        : 'text-white/90 hover:text-white hover:bg-white/10'
                                                                        }`}
                                                                >
                                                                    <subItem.icon className="w-4 h-4" />
                                                                    <span>{subItem.label}</span>
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        }

                                        return (
                                            <div key={item.id}>
                                                <Link
                                                    to={item.path}
                                                    onClick={(e) => handleProtectedClick(e, item)}
                                                    className={`flex items-center gap-x-3 px-4 py-3 rounded-xl text-base font-semibold transition-all backdrop-blur-sm ${isActive(item.path)
                                                        ? 'bg-white/20 text-white'
                                                        : 'text-white/90 hover:text-white hover:bg-white/10'
                                                        }`}
                                                >
                                                    <item.icon className="w-5 h-5" />
                                                    <span>{item.label}</span>
                                                </Link>
                                            </div>
                                        );
                                    })}

                                    <div className="pt-4 mt-4 border-t border-white/20">
                                        {!isLoggedIn ? (
                                            <div className="space-y-3">
                                                <div className="bg-white/10 rounded-xl p-2">
                                                    <p className="px-4 py-2 text-xs font-bold text-white/50 uppercase tracking-wider">Register As</p>
                                                    <div className="grid grid-cols-1 gap-1">
                                                        {registrationOptions.map((opt) => (
                                                            <Link
                                                                key={opt.id}
                                                                to={opt.path}
                                                                onClick={handleNavigation}
                                                                className="flex items-center gap-x-3 px-4 py-3 rounded-lg text-sm font-semibold text-white/90 hover:bg-white/10 transition-all"
                                                            >
                                                                <opt.icon className="w-5 h-5 text-white" />
                                                                <span>{opt.label}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                </div>
                                                <Link
                                                    to="/login"
                                                    onClick={handleNavigation}
                                                    className="block w-full text-center px-4 py-3 bg-white text-primary-blue rounded-xl font-bold hover:bg-gray-100 transition-all shadow-lg"
                                                >
                                                    Login
                                                </Link>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="mb-3 px-4 py-2 bg-white/10 backdrop-blur-sm rounded-lg">
                                                    <p className="text-white/80 text-sm">Logged in as:</p>
                                                    <p className="font-medium text-white">{displayName}</p>
                                                    <p className="text-white/60 text-xs mt-1">{currentUser?.role ? `Role: ${currentUser.role}` : ''}</p>
                                                </div>
                                                <button
                                                    onClick={handleLogoutClick}
                                                    className="flex items-center justify-center gap-x-2 w-full px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold hover:from-red-600 hover:to-red-700 transition-all shadow-lg"
                                                >
                                                    <FaSignOutAlt />
                                                    <span>Logout</span>
                                                </button>
                                                {isPendingPayment && (
                                                    <div
                                                        onClick={() => { setPendingNavPath(null); setShowPaymentModal(true); }}
                                                        className="mt-3 w-full bg-gradient-to-r from-teal-500 to-cyan-500 rounded-xl shadow-lg p-3 cursor-pointer hover:from-teal-600 hover:to-cyan-600 transition-all"
                                                    >
                                                        <p className="text-white/90 text-[11px] font-medium mb-1.5">⚠️ Activation payment pending</p>
                                                        <div className="flex items-center justify-between">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-white/60 line-through text-xs">₹999</span>
                                                                <span className="text-white font-extrabold text-lg">₹{signupPrice !== null ? signupPrice : '...'}</span>
                                                            </div>
                                                            <span className="px-3 py-1 bg-white text-teal-600 rounded-full text-xs font-bold shadow">Pay Now →</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </nav>

                    {/* Integrated Notice Board - Premium Live Updates Strip */}
                    {runningTitlesList.length > 0 && (
                        <div className="absolute bottom-0 left-0 right-0 z-40">
                            <div className="bg-black/40 backdrop-blur-md border-t border-white/10 w-full overflow-hidden">
                                <div className="max-w-[1600px] mx-auto flex items-center h-12 md:h-14">
                                    {/* Fixed Running Articles Label */}
                                    <div className="relative z-10 flex-shrink-0 pl-1 sm:pl-3 pr-1">
                                        <div className="inline-flex items-center gap-2.5 px-3 sm:px-4 py-1.5 bg-[#2a2a2a]/80 backdrop-blur-md rounded-lg border border-gray-600/50">
                                            <div className="w-2.5 h-2.5 bg-red-500 rounded-full shadow-[0_0_8px_rgba(239,68,68,0.6)]"></div>
                                            <span className="text-white text-[13px] sm:text-[15px] font-extrabold tracking-wide drop-shadow-sm">Running Articles</span>
                                        </div>
                                    </div>
                                    {/* Ticker Content - scrolls from the label */}
                                    <div className="flex-1 overflow-hidden relative group/ticker pr-6 sm:pr-12">
                                        <div className="animate-marquee inline-flex whitespace-nowrap items-center">
                                            {[...runningTitlesList, ...runningTitlesList].map((item, idx) => (
                                                <div
                                                    key={`${item.id || idx}-${idx}`}
                                                    className="inline-flex items-center gap-4 sm:gap-6 px-4 sm:px-8"
                                                >
                                                    <div className="flex items-center gap-3 sm:gap-4">
                                                        {/* Article Title */}
                                                        <span className="text-white font-bold text-[14px] sm:text-[15px] md:text-base tracking-wide cursor-default hover:text-blue-200 transition-colors">
                                                            {item.title || item.articleTitle}
                                                        </span>

                                                        {/* Deadline Badge */}
                                                        {(item.submission_deadline || item.submission_date || item.deadline || item.article_deadline || item.submissionDeadline) && (
                                                            <div className="flex items-center gap-1.5 sm:gap-2 bg-amber-500/10 px-2 sm:px-3 py-1 rounded-full border border-amber-400/20">
                                                                <span className="text-amber-400 font-bold text-[10px] sm:text-[11px] uppercase tracking-wider">Deadline</span>
                                                                <span className="text-white/90 text-[10px] sm:text-[11px] font-semibold">
                                                                    {new Date(
                                                                        item.submission_deadline ||
                                                                        item.submission_date ||
                                                                        item.deadline ||
                                                                        item.article_deadline ||
                                                                        item.submissionDeadline
                                                                    ).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    {/* Separator */}
                                                    <div className="flex items-center gap-1.5 opacity-30">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                                                        <div className="w-4 h-[1.5px] bg-white rounded-full"></div>
                                                        <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="relative z-20 h-[600px] flex items-center justify-center">
                        {slides.map((slide, index) => (
                            <div
                                key={index}
                                className={`absolute inset-0 transition-opacity duration-1000 flex items-center justify-center ${index === currentSlide ? 'opacity-100' : 'opacity-0'
                                    }`}
                            >
                                <div className="text-center text-white px-4 sm:px-6 w-full max-w-5xl mx-auto flex flex-col items-center">
                                    {isVerifiedEditorial && (
                                        <p className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 sm:mb-6 animate-fade-in text-white/95 drop-shadow-lg">
                                            Welcome, <span className="text-amber-400 drop-shadow-md">{displayName}</span>
                                        </p>
                                    )}
                                    <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold mb-4 sm:mb-6 tracking-tight leading-tight animate-fade-in drop-shadow-[0_4px_10px_rgba(0,0,0,0.5)]">
                                        {slide.title}
                                    </h1>
                                    <p className="text-xl sm:text-2xl md:text-3xl mb-8 sm:mb-10 text-white/95 font-semibold max-w-4xl mx-auto leading-relaxed animate-fade-in-delay drop-shadow-lg">
                                        {slide.subtitle.includes('Young India') ? (
                                            <>
                                                {slide.subtitle.split('Young India')[0]}
                                                <span className="relative inline-block mx-1">
                                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF9933] via-white to-[#128807] font-black drop-shadow-sm">
                                                        Young India
                                                    </span>
                                                    <div className="absolute -bottom-1.5 left-0 w-full h-[3px] sm:h-[4px] bg-gradient-to-r from-[#FF9933] via-white to-[#128807] rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.3)]"></div>
                                                </span>
                                                {slide.subtitle.split('Young India')[1]}
                                            </>
                                        ) : slide.subtitle}
                                    </p>
                                    <div className="flex flex-col sm:flex-row justify-center gap-4 animate-fade-in-delay-2">
                                        {!isLoggedIn ? (
                                            <Link
                                                to="/login"
                                                onClick={handleNavigation}
                                                className="px-8 sm:px-10 py-3.5 sm:py-4 bg-white text-primary-blue text-base sm:text-lg font-bold rounded-full hover:bg-slate-50 transition-all shadow-[0_8px_30px_rgb(0,0,0,0.2)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.4)] transform hover:-translate-y-1 inline-flex items-center justify-center gap-3"
                                            >
                                                Login <FaArrowRight className="text-sm" />
                                            </Link>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        ))}


                        <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 z-30 flex space-x-3">
                            {slides.map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentSlide(index)}
                                    className={`w-3 h-3 rounded-full transition-all ${index === currentSlide ? 'bg-white w-8' : 'bg-white/50'
                                        }`}
                                />
                            ))}
                        </div>
                    </div>

                </div>
            ) : (
                <nav className="sticky top-0 z-[100] bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 shadow-lg">
                    <div className="flex items-center h-16 lg:h-20 w-full px-4 sm:px-6 lg:px-8 min-[1201px]:px-12">
                        <div className="flex items-center flex-shrink-0 mr-4 lg:mr-8">
                            <Link to="/" className="flex items-center hover:opacity-90 transition-opacity" onClick={handleNavigation}>
                                <img src="/logo.png" alt="Paper Portal" className="h-8 sm:h-10 lg:h-12 mr-2 sm:mr-3 lg:mr-4" />
                            </Link>
                        </div>

                        <div className="flex-1 min-[1201px]:hidden" />

                        <ul className="hidden min-[1201px]:flex items-center justify-center flex-1 gap-x-1 2xl:gap-x-2">
                            {menuItems.map((item) => {
                                if (item.subItems) {
                                    const isItemActive = isDropdownActive(item);
                                    const hasSubItems = item.subItems.length > 0;

                                    return (
                                        <li key={item.id} className="relative group">
                                            <button
                                                onClick={(e) => {
                                                    if (item.protected && !isLoggedIn) {
                                                        setShowAuthModal(true);
                                                    } else {
                                                        toggleDropdown(item.id);
                                                    }
                                                }}
                                                className={`flex items-center gap-x-1.5 px-2.5 py-2 rounded-xl text-sm font-semibold transition-all duration-300 whitespace-nowrap ${isItemActive || openDropdown === item.id
                                                    ? 'bg-white/20 text-white shadow-lg'
                                                    : 'text-white/90 hover:text-white hover:bg-white/10 hover:shadow-lg'
                                                    }`}
                                            >
                                                <item.icon className="w-4 h-4" />
                                                <span>{item.label}</span>
                                                {hasSubItems && (
                                                    <FaChevronDown className={`w-3 h-3 transition-transform ${openDropdown === item.id ? 'rotate-180' : ''}`} />
                                                )}
                                            </button>

                                            {openDropdown === item.id && hasSubItems && (
                                                <ul className="absolute left-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-2xl py-2 z-50">
                                                    {item.subItems.map((subItem) => (
                                                        <li key={subItem.id}>
                                                            <Link
                                                                to={subItem.path}
                                                                onClick={(e) => handleSubItemClick(e, subItem, item)}
                                                                className={`flex items-center gap-x-3 px-4 py-3 text-sm font-medium transition-all duration-200 ${isActive(subItem.path)
                                                                    ? 'bg-gradient-to-r from-blue-50 to-primary-blue/20 text-primary-blue border-r-4 border-primary-blue'
                                                                    : 'text-gray-700 hover:bg-blue-50 hover:text-primary-blue'
                                                                    }`}
                                                            >
                                                                <subItem.icon className="w-4 h-4" />
                                                                <span>{subItem.label}</span>
                                                            </Link>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    );
                                }

                                return (
                                    <li key={item.id}>
                                        <Link
                                            to={item.path}
                                            onClick={(e) => handleProtectedClick(e, item)}
                                            className={`flex items-center gap-x-1.5 px-2.5 py-2 rounded-xl text-sm font-semibold transition-all duration-300 whitespace-nowrap ${isActive(item.path)
                                                ? 'bg-white/20 text-white shadow-lg'
                                                : 'text-white/90 hover:text-white hover:bg-white/10 hover:shadow-lg'
                                                }`}
                                        >
                                            <item.icon className="w-4 h-4" />
                                            <span>{item.label}</span>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>

                        <div className="flex-1 min-[1201px]:hidden" />

                        <div className="hidden min-[1201px]:flex items-center gap-x-2 flex-shrink-0">
                            {!isLoggedIn ? (
                                <div className="flex items-center gap-x-2">
                                    <div className="relative group">
                                        <button
                                            onClick={() => toggleDropdown('register-dropdown')}
                                            className={`flex items-center gap-x-1.5 px-4 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap ${openDropdown === 'register-dropdown'
                                                ? 'bg-white text-primary-blue shadow-lg border border-primary-blue/10'
                                                : 'bg-white text-primary-blue border border-primary-blue hover:bg-blue-50'
                                                }`}
                                        >
                                            <FaUserPlus className="w-4 h-4" />
                                            <span>Register</span>
                                            <FaChevronDown className={`w-3 h-3 transition-transform ${openDropdown === 'register-dropdown' ? 'rotate-180' : ''}`} />
                                        </button>

                                        {openDropdown === 'register-dropdown' && (
                                            <ul className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-2xl py-2 z-50 animate-fade-in border border-gray-100">
                                                {registrationOptions.map((opt) => (
                                                    <li key={opt.id}>
                                                        <Link
                                                            to={opt.path}
                                                            onClick={handleNavigation}
                                                            className="flex items-center gap-x-3 px-4 py-3 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:text-primary-blue transition-all duration-200"
                                                        >
                                                            <opt.icon className="w-4 h-4 text-primary-blue" />
                                                            <span>{opt.label}</span>
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                    <Link
                                        to="/login"
                                        onClick={handleNavigation}
                                        className="flex items-center gap-x-1.5 px-4 py-2.5 rounded-xl text-sm font-bold bg-white text-primary-blue hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap border border-primary-blue"
                                    >
                                        Login
                                    </Link>
                                </div>
                            ) : (
                                <div className="flex items-center gap-x-2">
                                    <div className="text-white text-sm font-medium bg-white/10 px-3 py-2 rounded-lg">
                                        <span className="font-semibold">{displayName}</span>
                                    </div>
                                    <button
                                        onClick={handleLogoutClick}
                                        className="flex items-center gap-x-1.5 px-4 py-2.5 rounded-xl text-sm font-bold bg-red-600 text-white hover:bg-red-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap"
                                    >
                                        <FaSignOutAlt className="w-4 h-4" />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="min-[1201px]:hidden ml-4 mobile-toggle">
                            <button
                                onClick={() => setIsOpen(!isOpen)}
                                className="p-3 rounded-xl text-white hover:bg-white/10 transition-all duration-300"
                            >
                                {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
                            </button>
                        </div>
                    </div>

                    {isOpen && (
                        <div className="min-[1201px]:hidden absolute left-0 right-0 top-full z-40 mx-4 mb-4 bg-white shadow-2xl rounded-xl mt-2 border border-gray-100 mobile-menu">
                            <div className="px-4 pt-4 pb-6 space-y-2 max-h-[80vh] overflow-y-auto">
                                {menuItems.map((item) => {
                                    if (item.subItems) {
                                        const isItemActive = isDropdownActive(item);
                                        const hasSubItems = item.subItems.length > 0;

                                        return (
                                            <div key={item.id}>
                                                <button
                                                    onClick={() => toggleDropdown(item.id)}
                                                    className={`flex items-center justify-between w-full px-4 py-3 rounded-xl text-base font-semibold transition-all ${isItemActive || openDropdown === item.id
                                                        ? 'bg-primary-blue text-white'
                                                        : 'text-gray-700 hover:bg-blue-50'
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-x-3">
                                                        <item.icon className="w-5 h-5" />
                                                        <span>{item.label}</span>
                                                    </div>
                                                    {hasSubItems && (
                                                        <FaChevronDown className={`w-4 h-4 transition-transform ${openDropdown === item.id ? 'rotate-180' : ''}`} />
                                                    )}
                                                </button>

                                                {openDropdown === item.id && hasSubItems && (
                                                    <div className="pl-8 mt-2 space-y-2">
                                                        {item.subItems.map((subItem) => (
                                                            <Link
                                                                key={subItem.id}
                                                                to={subItem.path}
                                                                onClick={(e) => handleSubItemClick(e, subItem, item)}
                                                                className={`flex items-center gap-x-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${isActive(subItem.path)
                                                                    ? 'bg-primary-blue text-white'
                                                                    : 'text-gray-600 hover:bg-blue-50'
                                                                    }`}
                                                            >
                                                                <subItem.icon className="w-4 h-4" />
                                                                <span>{subItem.label}</span>
                                                            </Link>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }

                                    return (
                                        <div key={item.id}>
                                            <Link
                                                to={item.path}
                                                onClick={(e) => handleProtectedClick(e, item)}
                                                className={`flex items-center gap-x-3 px-4 py-3 rounded-xl text-base font-semibold transition-all ${isActive(item.path)
                                                    ? 'bg-primary-blue text-white'
                                                    : 'text-gray-700 hover:bg-blue-50'
                                                    }`}
                                            >
                                                <item.icon className="w-5 h-5" />
                                                <span>{item.label}</span>
                                            </Link>
                                        </div>
                                    );
                                })}

                                <div className="pt-4 mt-4 border-t border-gray-200">
                                    {!isLoggedIn ? (
                                        <div className="space-y-3">
                                            <div className="bg-gray-50 rounded-xl p-2 border border-gray-100">
                                                <p className="px-4 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Register As</p>
                                                <div className="grid grid-cols-1 gap-1">
                                                    {registrationOptions.map((opt) => (
                                                        <Link
                                                            key={opt.id}
                                                            to={opt.path}
                                                            onClick={handleNavigation}
                                                            className="flex items-center gap-x-3 px-4 py-3 rounded-lg text-sm font-semibold text-gray-700 hover:bg-blue-50 transition-all"
                                                        >
                                                            <opt.icon className="w-5 h-5 text-primary-blue" />
                                                            <span>{opt.label}</span>
                                                        </Link>
                                                    ))}
                                                </div>
                                            </div>
                                            <Link
                                                to="/login"
                                                onClick={handleNavigation}
                                                className="block w-full text-center px-4 py-3 bg-primary-blue text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg"
                                            >
                                                Login
                                            </Link>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="mb-3 px-4 py-2 bg-blue-50 rounded-lg">
                                                <p className="text-gray-600 text-sm">Logged in as:</p>
                                                <p className="font-medium text-gray-900">{displayName}</p>
                                                <p className="text-gray-500 text-xs mt-1">{currentUser?.role ? `Role: ${currentUser.role}` : ''}</p>
                                            </div>
                                            <button
                                                onClick={handleLogoutClick}
                                                className="flex items-center justify-center gap-x-2 w-full px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all shadow-lg"
                                            >
                                                <FaSignOutAlt />
                                                <span>Logout</span>
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </nav>
            )}

            {
                showAuthModal && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[100] p-4">
                        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
                            <div className="relative p-8 text-center">
                                <button
                                    onClick={() => setShowAuthModal(false)}
                                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                                >
                                    <FaTimes size={20} />
                                </button>

                                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <FaUserPlus className="w-8 h-8 text-primary-blue" />
                                </div>

                                <h3 className="text-2xl font-bold text-gray-900 mb-2">Login Required</h3>
                                <p className="text-gray-600 mb-8">
                                    You need to log in or register first to access this feature.
                                </p>

                                <div className="flex flex-col gap-3">
                                    <Link
                                        to="/login"
                                        onClick={() => setShowAuthModal(false)}
                                        className="w-full py-4 bg-primary-blue text-white rounded-2xl font-bold hover:bg-blue-700 transition-all shadow-lg flex items-center justify-center gap-2"
                                    >
                                        <FaUser /> Login Now
                                    </Link>
                                    <div className="flex items-center gap-3 my-2">
                                        <div className="h-px flex-1 bg-gray-200"></div>
                                        <span className="text-gray-400 text-xs font-semibold uppercase">Register As</span>
                                        <div className="h-px flex-1 bg-gray-200"></div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-2">
                                        {registrationOptions.map(option => (
                                            <Link
                                                key={option.id}
                                                to={option.path}
                                                onClick={() => setShowAuthModal(false)}
                                                className={`flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-100 hover:border-primary-blue/30 hover:bg-blue-50 transition-all group`}
                                            >
                                                <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${option.gradient} flex items-center justify-center text-white shrink-0`}>
                                                    <option.icon className="w-4 h-4" />
                                                </div>
                                                <div className="text-left flex-1">
                                                    <div className="font-bold text-gray-900 text-xs">{option.label}</div>
                                                    <div className="text-[10px] text-gray-500">{option.description}</div>
                                                </div>
                                                <FaArrowRight className="w-3 h-3 text-gray-300 group-hover:text-primary-blue transition-colors" />
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Payment Success Modal - centered popup after payment completes */}
            {
                showPaymentSuccessModal && (
                    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
                        <div className="bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 text-center animate-scale-in">
                            <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <FaCheckCircle className="text-6xl text-green-500" />
                            </div>
                            <h2 className="text-2xl font-extrabold text-gray-900 mb-2">Payment Complete!</h2>
                            <p className="text-lg font-semibold text-green-600 mb-4">Account Activated</p>
                            <p className="text-gray-500 text-sm mb-8">Your account has been fully activated. You now have access to all platform features.</p>
                            <button
                                onClick={() => {
                                    setShowPaymentSuccessModal(false);
                                    navigate(paymentSuccessNavPath || '/');
                                }}
                                className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold text-lg hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                )
            }
        </>
    );
};

export default Navbar;
